import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";

import { currentTenant, userid } from '../../Common/Credentials.js';
import { spin } from '../../Common/NewLoader.jsx';
import { appmasterid } from '../../Common/Permissions.js';
import Header from '../../Component/Header.js';
import Menu from '../../Component/Menu.js';
import Footer from "../../Component/Footer.js";
import BankServices from './BankServices.js';

import EditIcon from "../../images/Icons/edit.png";
import DeleteIcon from "../../images/Icons/delete.png";
import Breadcrumb from "../../NewUI/NewComponents/Breadcrumb.jsx";

const Bank = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'accname', direction: 'asc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshData = useCallback(() => {
    setLoading(true);

    BankServices.getBanks(currentTenant)
      .then((response) => {
        const sortedData = response.data.sort((a, b) => a.accname?.localeCompare(b.accname));
        setData(sortedData);
        setFilteredData(sortedData);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshData();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshData]);

  useEffect(() => {
    updateFilteredData();
  }, [data, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = data;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = data.filter(item =>
        item.accname?.toLowerCase().includes(lowerSearchTerm) ||
        item.bankname?.toLowerCase().includes(lowerSearchTerm) ||
        item.accno?.toLowerCase().includes(lowerSearchTerm) ||
        item.branchname?.toLowerCase().includes(lowerSearchTerm) ||
        item.ifsc?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const deleteBank = (id) => {
    Swal.fire({
      title: "Delete Bank?",
      text: "Are you sure you want to delete this bank?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        BankServices.deleteBank(id)
          .then(() => {
            Swal.fire({
              title: "Bank deleted successfully",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              setLoading(true);
              refreshData();
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: "Error occurred while deleting",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              });
            }
          });
      }
    });
  };

  const setActive = (id) => {
    Swal.fire({
      title: "Set as active bank?",
      text: "Are you sure you want to set as Active bank?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        BankServices.setActive(id)
          .then(() => {
            Swal.fire({
              title: "Bank is successfully changed as active",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              setLoading(true);
              refreshData();
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: "Error occurred while setting active",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              });
            }
          });
      }
    });
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} banks
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No banks found matching your search' : 'No banks available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'accname' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('accname')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Account Name
                    <SortIndicator columnKey="accname" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'bankname' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('bankname')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Bank Name
                    <SortIndicator columnKey="bankname" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'accno' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('accno')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Account Number
                    <SortIndicator columnKey="accno" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'branchname' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('branchname')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Branch Name
                    <SortIndicator columnKey="branchname" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'ifsc' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('ifsc')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    IFSC Code
                    <SortIndicator columnKey="ifsc" />
                  </div>
                </th>
                {(appmasterid === "2" || appmasterid === "3") && (
                  <th
                    className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'status' ? 'UI-th-sorted' : ''}`}
                    onClick={() => sortData('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="UI-th-content">
                      Status
                      <SortIndicator columnKey="status" />
                    </div>
                  </th>
                )}
                {(appmasterid === "2" || appmasterid === "3") && (
                  <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((item) => (
                <tr
                  key={item.id}
                  className={`UI-table-row ${hoveredRow === item.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* Account Name Cell */}
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.accname, searchTerm)}
                  </td>

                  {/* Bank Name Cell */}
                  <td
                    className={`UI-td ${hoveredRow === item.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.bankname, searchTerm)}
                  </td>

                  {/* Account Number Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.accno, searchTerm)}
                  </td>

                  {/* Branch Name Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.branchname, searchTerm)}
                  </td>

                  {/* IFSC Code Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.ifsc, searchTerm)}
                  </td>

                  {/* Status Cell */}
                  {(appmasterid === "2" || appmasterid === "3") && (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {item.status === "ACTIVE" ? (
                        <span className="UI-status-badge UI-status-active">
                          {item.status}
                        </span>
                      ) : (
                        <button
                          onClick={() => setActive(item.id)}
                          className={`UI-btn UI-btn-outline ${hoveredAction === `set-active-${item.id}` ? 'UI-btn-outline-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                          style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                          onMouseEnter={() => setHoveredAction(`set-active-${item.id}`)}
                          onMouseLeave={() => setHoveredAction(null)}
                        >
                          {item.status}
                        </button>
                      )}
                    </td>
                  )}

                  {/* Actions Cell */}
                  {(appmasterid === "2" || appmasterid === "3") && (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <div className="UI-action-buttons">
                        {/* Edit Bank */}
                        <Tooltip title="Edit Bank Details" arrow slotProps={{
                          popper: {
                            sx: { pointerEvents: 'none' },
                          },
                        }}>
                          <Link
                            to="/addbank"
                            state={{
                              id: item.id,
                              pagename: "Edit Bank Details",
                            }}
                            className={`UI-action-button ${hoveredAction === `edit-${item.id}` ? 'UI-edit-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`edit-${item.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <img
                              src={EditIcon}
                              alt="Edit"
                              className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `edit-${item.id}` ? 'UI-action-icon-hover' : ''}`}
                            />
                          </Link>
                        </Tooltip>

                        {/* Delete Bank */}
                        <Tooltip title="Delete Bank" arrow slotProps={{
                          popper: {
                            sx: { pointerEvents: 'none' },
                          },
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteBank(item.id);
                            }}
                            className={`UI-action-button ${hoveredAction === `delete-${item.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`delete-${item.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <img
                              src={DeleteIcon}
                              alt="Delete"
                              className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `delete-${item.id}` ? 'UI-action-icon-hover' : ''}`}
                            />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
          <div className="UI-card">
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
            <Breadcrumb breadcrumbs={[{name:"Home",path:"/masters"},{name:"Banks",path:null}]}/>

              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    Banks
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage bank accounts
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder="Search banks..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder="Search banks..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if (searchTerm.trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* New Bank button */}
                    {(appmasterid === "2" || appmasterid === "3") && (
                      <Link
                        to="/addbank"
                        state={{
                          id: -1,
                          pagename: "Add Bank Details",
                        }}
                        className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                        onMouseEnter={() => setHovered("new")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i className="fa fa-plus" /> {isMobile ? "New" : "New Bank"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="UI-loading-container">
                {spin}
              </div>
            ) : (
              renderTable()
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Bank;